import type { PropertySchema } from '@takeshape/schema';
import type { ImmutableMap } from 'app/common/utils/immutable-types';
import { type List, Map } from 'immutable';
import toPath from 'lodash/toPath';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';
import { setIn } from '../common/utils/immutable-util.js';
import { NAME } from './constants.js';
import { getFormPath, PROPERTIES } from './paths.js';
import type { FormState } from './types.js';
import { splitRef } from './util/refs.js';
import { serializeData } from './util/serializer.js';
import { flattenStructuralChanges } from './util/structural-changes.js';

const emptyMap: ImmutableMap<any> = Map() as any;
const getState = (state: any) => state[NAME];
const getPath = (_: unknown, { path }: { path?: any }) => path;
const getOnlyDirty = (_: unknown, { onlyDirty }: { onlyDirty?: boolean }) => onlyDirty;
const getSchemaRef = (_: unknown, { schemaRef }: { schemaRef?: string }) => schemaRef;

export type GetFormProps = {
  formName: string;
  selector?: (state: unknown) => ImmutableMap<FormState>;
  path?: string | string[];
};

export const getForm = (state: unknown, { selector, formName }: GetFormProps): ImmutableMap<FormState> => {
  const rootSelector = selector ?? getState;
  return rootSelector(state).getIn(getFormPath(formName)) as ImmutableMap<FormState>;
};

export const getFormSchema = createCachedSelector(
  [getForm, getSchemaRef],
  (form: ImmutableMap<FormState>, schemaRef?: string): ImmutableMap<PropertySchema> | undefined => {
    if (form) {
      const path = schemaRef ? ['schema'].concat(splitRef(schemaRef)) : ['schema'];
      return form.getIn(path);
    }
  }
)((_, props) => props.formName);

export const getInitialData = createSelector([getForm, getPath], (form, path) =>
  form?.getIn(['initialData'].concat(toPath(path)))
);

export const getData = createSelector([getForm, getPath], (form, path) => form?.getIn(['data'].concat(toPath(path))));

export const getValues = createSelector([getForm, getOnlyDirty], (form, onlyDirty) => {
  let data: any = form.get('data');

  if (onlyDirty) {
    const dirty = form.get('dirty');
    data = Map({}).withMutations((mutable) => {
      if (dirty) {
        // Copy dirty array properties
        dirty.forEach((pathStr?: string) => {
          const path = toPath(pathStr);
          if (path.length > 1) {
            const prop = path[0];
            if (!mutable.has(prop)) {
              mutable.set(prop, data.get(prop));
            }
          }
        });
        // Copy dirty properties and replace undefined values with a sentinel null value
        dirty.forEach((pathStr?: string) => {
          const path = toPath(pathStr);
          const value = data.getIn(path);
          // It's possible the parent was removed
          if (data.hasIn(path.slice(0, -1))) {
            setIn(mutable, path, value === undefined ? null : value);
          }
        });
      }
    });
  }

  return serializeData(form.get('schema'), data, true);
});

export const getUi = createSelector([getForm, getPath], (form, path) =>
  form?.getIn(['ui'].concat(toPath(path), [PROPERTIES]))
);

export const getBusy = createSelector([getForm, getPath], (form) => form?.getIn(['busy']));

export const getErrors = createSelector([getForm], (form) => form?.get('errors', emptyMap));

export const getHiddenErrors = createSelector([getForm], (form) => form?.get('hiddenErrors', emptyMap));

export const getAllErrors = createSelector([getErrors, getHiddenErrors], (errors, hiddenErrors) =>
  errors ? errors.merge(hiddenErrors) : undefined
);

export const getAutoSubmit = createSelector([getForm], (form) => form?.get('autoSubmit', false));

export const getValidateOnBlur = createSelector([getForm], (form) => form?.get('validateOnBlur', false));

const getSubmitErrors = createSelector([getForm], (form) => form?.get('submitErrors', emptyMap));

export const getError = createSelector([getErrors, getSubmitErrors, getPath], (errors, submitErrors, path) => {
  const errorPath = toPath(path).join('.');
  return submitErrors?.get(errorPath) ?? errors?.get(errorPath);
});

export const getDirty = createSelector([getForm], (form) => form?.get('dirty'));
export const getDirtyJS = createSelector([getForm], (form) => form?.get('dirty').toArray());

export const getDefinitions = createSelector([getForm], (form) => form?.getIn(['schema', 'definitions']));

export const makeValueSelector = () => {
  return createSelector([getData, getUi, getError], (value, ui, error) => ({
    value: value ?? '',
    ui,
    error
  }));
};

export const makeArrayLengthSelector = () => {
  return createSelector(getData, (data) => (data ? (data as List<any>).size : 0));
};

export const getStructuralChanges = createSelector([getForm], (form) => {
  const structuralChanges = form.get('structuralChanges');
  if (!structuralChanges) {
    return [];
  }

  return flattenStructuralChanges(structuralChanges, [], []);
});
